/**
 * Enum representing the different variants of the ActionButton.
 * Enum cases are in capital letters as per the company's standards.
 */
export enum ActionButtonVariant {
    /** 
     * Primary button, typically used for main actions or emphasis.
     * This is often the most important button on the page, drawing the user's attention.
     */
    PRIMARY,

    /** 
     * Secondary button, used for less important actions.
     * It typically appears more subdued in color and is used for secondary actions.
     */
    SECONDARY,

    /** 
     * Close button, typically used to close modals or dismiss items.
     * Commonly associated with an "X" icon or other simple indicators for closing actions.
     */
    CLOSE,

    /** 
     * Create button, used for creating new items or initiating new actions.
     * This button typically has a positive or action-oriented color, like green, to indicate creation or addition.
     */
    CREATE,

    /** 
     * Large button, used for more prominent or emphasized actions.
     * This button size is larger than the standard button and is used to highlight a primary or important action.
     */
    LARGE,

    /** 
     * Small button, typically used for compact UI spaces.
     * Smaller than the default button size, ideal for less important actions or when space is constrained.
     */
    SMALL,

    /** 
     * Cancel button, typically used to cancel or dismiss an action or dialog.
     * Often associated with a neutral or negative action and can be used in forms or modal dialogs.
     */
    CANCEL,

    BLUE,

    GREEN,

    GRAY,

    SAVE
}

/**
 * Interface representing the metadata for a button variant.
 * Contains the button's label, className, and optional icon.
 * This interface is used to define the structure for button-related data
 * and is leveraged when rendering buttons based on different variants.
 */
interface ButtonMetadata {
    /** 
     * The label that will be displayed on the button.
     * This is a string that represents the text that the user will see.
     * Example: "Primary", "Cancel", "Create".
     */
    label: string

    /** 
     * The className to be applied to the button's DOM element.
     * This is used to style the button via CSS and helps in distinguishing the button's appearance based on its variant.
     * Example: "primary", "secondary", "close".
     */
    className: string

    /** 
     * The icon to be used for the button.
     * This is an optional string representing the icon class, typically used for icon libraries (e.g., Font Awesome, Material Icons).
     * If no icon is used, this can be an empty string.
     * Example: "primary-icon", "close-icon".
     */
    icon?: string
}

/**
 * Get metadata (label, className, icon) for a given button variant.
 * This metadata is used to style and define the button's content.
 * 
 * @param variant - The variant enum representing the button style (e.g., PRIMARY, SECONDARY).
 * @returns {label, className, icon} - The corresponding metadata for the given variant.
 */
export function getButtonVariantMetadata(variant: ActionButtonVariant): ButtonMetadata {
    switch (variant) {
        case ActionButtonVariant.PRIMARY:
            return { label:"", className: "bg-red-500 hover:bg-red-600 focus:bg-red-700 focus:ring-red-500 text-white rounded-full py-3" }
        case ActionButtonVariant.SECONDARY:
            return { label: "Secondary", className: "secondary", icon: "secondary-icon" }
        case ActionButtonVariant.CLOSE:
            return { label: "Close", className: "close", icon: "close-icon" }
        case ActionButtonVariant.CREATE:
            return { label: "Create", className: "create", icon: "create-icon" }
        case ActionButtonVariant.LARGE:
            return { label: "Large", className: "large", icon: "large-icon" }
        case ActionButtonVariant.SMALL:
            return { label: "Small", className: "small", icon: "small-icon" }
        case ActionButtonVariant.CANCEL:
            return { label: "Cancel", className: "bg-gray-500 text-white px-3 py-2 rounded flex-1 text-sm hover:bg-gray-600 transition-colors", icon: "cancel-icon" }
        case ActionButtonVariant.BLUE:
            return { label:"", className: "bg-blue-500 hover:bg-blue-600 focus:bg-blue-700 focus:ring-blue-500 text-white rounded-lg py-2.5" }
        case ActionButtonVariant.GREEN:
            return { label:"", className: "bg-green-500 hover:bg-green-600 focus:bg-green-700 focus:ring-green-500 text-white rounded-lg py-2.5" }
        case ActionButtonVariant.GRAY:
            return { label:"", className: "bg-gray-100 hover:bg-gray-200 focus:bg-gray-300 focus:ring-gray-200 text-gray-700 text-black rounded-lg py-2.5" }
        case ActionButtonVariant.SAVE:
            return { label:"Save", className: "bg-blue-500 text-white px-3 py-2 rounded flex-1 text-sm flex items-center justify-center hover:bg-blue-600 transition-colors disabled:opacity-50" }
        default:
            return { label: "Unknown", className: "unknown", icon: "unknown-icon" }
    }
}